import { useForm } from 'react-hook-form';
import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, FormLabel, OutlinedInput } from '@mui/material';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import AppAutocompleteWithActions from '@components/app-autocomplete-with-actions/app-autocomplete-with-actions.tsx';
import AppDialog from '@components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@components/delete-dialog/delete-dialog.tsx';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import { CrudActionEnum } from '@models/global.model.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';

interface ServiceOption {
  id: number;
  title: string;
}

interface AmenityOption {
  id: number;
  name: string;
}

const hotelServicesData = [
  { id: 1, title: 'WiFi' },
  { id: 2, title: 'Parking' },
  { id: 3, title: 'Stopping' }
];

const roomFacilitiesData = [
  { id: 1, title: 'Clean' },
  { id: 2, title: 'Pickup' },
  { id: 3, title: 'Dirty' }
];

// Localized options (used when the `language` prop is passed) — title is an object.
const localizedServicesData = [
  { id: 1, title: { en: 'Breakfast', ar: 'إفطار' } },
  { id: 2, title: { en: 'Spa', ar: 'سبا' } },
  { id: 3, title: { en: 'Laundry', ar: 'غسيل' } }
];

// Options for AppAutocompleteWithActions use `name` instead of `title`.
const amenitiesData = [
  { id: 1, name: 'Pool' },
  { id: 2, name: 'Gym' },
  { id: 3, name: 'Sauna' }
];

const Section = ({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="w-full lg:w-1/2">
    <p className="font-medium mb-2 text-gray-700">{title}</p>
    {children}
  </div>
);

function AutocompleteComponent() {
  const { t } = useTranslation();
  const {
    formState: { errors },
    control
  } = useForm<any>({ mode: 'onTouched' });
  const getErrorMessage = useErrorMessage();
  const [lazyLoading, setLazyLoading] = useState(false);

  // "Add with dialog" flow — fully local, mock data (no API calls).
  const [dialogOptions, setDialogOptions] =
    useState<ServiceOption[]>(hotelServicesData);
  const [dialogSelected, setDialogSelected] = useState<ServiceOption[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogValue, setAddDialogValue] = useState('');

  const openAddDialog = (title: string) => {
    setAddDialogValue(title);
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setAddDialogValue('');
  };

  const handleAddDialogSave = () => {
    const title = addDialogValue.trim();
    if (!title) return;
    const newItem: ServiceOption = { id: Date.now(), title };
    setDialogOptions((prev) => [...prev, newItem]);
    setDialogSelected((prev) => [...prev, newItem]);
    closeAddDialog();
  };

  // AppAutocompleteWithActions — inline add / edit / delete, all local mock data.
  // Selection is controlled (selectedList + onChange) — this is the documented
  // usage; without it the component clears the selection whenever options change.
  type AmenitySetter = Dispatch<SetStateAction<AmenityOption[]>>;
  const [amenityOptions, setAmenityOptions] =
    useState<AmenityOption[]>(amenitiesData);
  const [amenityMultiSelected, setAmenityMultiSelected] = useState<
    AmenityOption[]
  >([]);
  const [amenitySingleSelected, setAmenitySingleSelected] = useState<
    AmenityOption[]
  >([]);
  const [amenityDialogOpen, setAmenityDialogOpen] = useState(false);
  const [amenityDialogValue, setAmenityDialogValue] = useState('');
  const [amenityEditId, setAmenityEditId] = useState<number | null>(null);
  const [amenityActiveSetter, setAmenityActiveSetter] =
    useState<AmenitySetter | null>(null);
  const [amenityActiveMultiple, setAmenityActiveMultiple] = useState(true);
  const [amenityDeleteTarget, setAmenityDeleteTarget] =
    useState<AmenityOption | null>(null);

  // One handler factory per instance, so the dialog knows which selection to
  // update on save (the instance that triggered the action).
  const makeAmenityHandler =
    (setter: AmenitySetter, multiple: boolean) =>
    (value: any, type: CrudActionEnum) => {
      if (type === CrudActionEnum.Add && typeof value === 'string') {
        setAmenityEditId(null);
        setAmenityDialogValue(value);
        setAmenityActiveSetter(() => setter);
        setAmenityActiveMultiple(multiple);
        setAmenityDialogOpen(true);
      } else if (type === CrudActionEnum.Edit && value) {
        setAmenityEditId(value.id);
        setAmenityDialogValue(value.name);
        setAmenityActiveSetter(() => setter);
        setAmenityActiveMultiple(multiple);
        setAmenityDialogOpen(true);
      } else if (type === CrudActionEnum.Delete && value) {
        setAmenityDeleteTarget(value);
      }
    };

  const closeAmenityDialog = () => {
    setAmenityDialogOpen(false);
    setAmenityDialogValue('');
    setAmenityEditId(null);
    setAmenityActiveSetter(null);
  };

  const saveAmenity = () => {
    const name = amenityDialogValue.trim();
    if (!name) return;
    if (amenityEditId != null) {
      const editId = amenityEditId;
      const rename = (list: AmenityOption[]) =>
        list.map((o) => (o.id === editId ? { ...o, name } : o));
      setAmenityOptions(rename);
      setAmenityMultiSelected(rename);
      setAmenitySingleSelected(rename);
    } else {
      const newItem: AmenityOption = { id: Date.now(), name };
      setAmenityOptions((prev) => [...prev, newItem]);
      // Select the freshly created item in the instance that opened the dialog.
      amenityActiveSetter?.((prev) =>
        amenityActiveMultiple ? [...prev, newItem] : [newItem]
      );
    }
    closeAmenityDialog();
  };

  const confirmDeleteAmenity = () => {
    if (amenityDeleteTarget) {
      const id = amenityDeleteTarget.id;
      const remove = (list: AmenityOption[]) => list.filter((o) => o.id !== id);
      setAmenityOptions(remove);
      setAmenityMultiSelected(remove);
      setAmenitySingleSelected(remove);
    }
    setAmenityDeleteTarget(null);
  };

  return (
    <div className="flex flex-col gap-12">
      {/* ============ AppAutocompleteV2 ============ */}
      <div className="flex flex-col gap-6">
        <h2 className="text-xl font-bold text-gray-800">AppAutocompleteV2</h2>

        <div className="w-full flex flex-wrap justify-between items-start gap-6">
          <Section title="🟢 Single select">
            <AppAutocompleteV2
              name="hotelServicesSingle"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              placeholder={t('placeholder_included_services')}
            />
          </Section>

          <Section title="🔵 Multiple select">
            <AppAutocompleteV2
              name="hotelServicesMultiple"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              multiple
            />
          </Section>

          <Section title="🟣 Multiple with chips">
            <AppAutocompleteV2
              name="roomFacilitiesWithChips"
              control={control}
              label={t('label_room_facilities')}
              options={roomFacilitiesData}
              multiple
              withChip
            />
          </Section>

          <Section title="✅ Pre-selected (selectedList)">
            <AppAutocompleteV2
              name="hotelServicesPreselected"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              multiple
              selectedList={[hotelServicesData[0], hotelServicesData[2]]}
            />
          </Section>

          <Section title="🌐 Localized options (language)">
            <AppAutocompleteV2
              name="localizedServices"
              control={control}
              label={t('label_hotel_services')}
              options={localizedServicesData}
              language="en"
              multiple
            />
          </Section>

          <Section title="⏳ Loading">
            <AppAutocompleteV2
              name="servicesLoading"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              loading
            />
          </Section>

          <Section title="🚫 Disabled">
            <AppAutocompleteV2
              name="servicesDisabled"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              disabled
              selectedList={[hotelServicesData[1]]}
            />
          </Section>

          <Section title={`🔄 Lazy load on open${lazyLoading ? ' (loading…)' : ''}`}>
            <AppAutocompleteV2
              name="servicesLazy"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              loading={lazyLoading}
              onOpen={() => setLazyLoading(true)}
              onClose={() => setLazyLoading(false)}
            />
          </Section>

          <Section title="➕ Add item (CrudActionEnum.Add)">
            <AppAutocompleteV2
              name="packageServicesAdd"
              control={control}
              label={
                <span className="flex items-center">
                  <span className="required-symbole">*</span>
                  {t('label_package_included_service')}
                  <InfoTooltip text="rooms_packages_management_package_included_service_tooltip" />
                </span>
              }
              placeholder={t('placeholder_included_services')}
              options={[]}
              multiple
              addItemConfig={{
                action: CrudActionEnum.Add,
                callback: () => {
                  // createIncludedService(data);
                }
              }}
            />
          </Section>

          <Section title="🗔 Add with dialog (CrudActionEnum.AddWithDialog)">
            <AppAutocompleteV2
              name="packageServicesAddDialog"
              control={control}
              label={t('label_package_included_service')}
              placeholder="Type a new value, then click +Add"
              options={dialogOptions}
              selectedList={dialogSelected}
              multiple
              addItemConfig={{
                action: CrudActionEnum.AddWithDialog,
                callback: (data) => openAddDialog(data.title)
              }}
            />
          </Section>

          <Section title="❗ Error / required (validationProps)">
            <AppAutocompleteV2
              name="servicesRequired"
              control={control}
              label={t('label_hotel_services')}
              options={hotelServicesData}
              validationProps={{
                errors,
                errorMessages: {
                  servicesRequired: { required: 'validation_service_required' }
                },
                getErrorMessage,
                validationRules: { required: true }
              }}
            />
          </Section>
        </div>
      </div>

      {/* ============ AppAutocompleteWithActions ============ */}
      <div className="flex flex-col gap-6">
        <h2 className="text-xl font-bold text-gray-800">
          AppAutocompleteWithActions
        </h2>
        <p className="text-sm text-gray-400 -mt-4">
          Inline add / edit / delete on each option via{' '}
          <code>itemActionHandler(value, CrudActionEnum)</code>.
        </p>

        <div className="w-full flex flex-wrap justify-between items-start gap-6">
          <Section title="🟣 Multiple + actions (Add / Edit / Delete)">
            <AppAutocompleteWithActions
              name="amenitiesActions"
              control={control}
              label={t('label_room_facilities')}
              placeholder="Type a name, then +Add — or hover an option to edit/delete"
              options={amenityOptions}
              multiple
              selectedList={amenityMultiSelected}
              onChange={setAmenityMultiSelected}
              itemActionHandler={makeAmenityHandler(
                setAmenityMultiSelected,
                true
              )}
            />
          </Section>

          <Section title="🟢 Single + actions">
            <AppAutocompleteWithActions
              name="amenitiesActionsSingle"
              control={control}
              label={t('label_room_facilities')}
              options={amenityOptions}
              multiple={false}
              selectedList={amenitySingleSelected}
              onChange={setAmenitySingleSelected}
              itemActionHandler={makeAmenityHandler(
                setAmenitySingleSelected,
                false
              )}
            />
          </Section>

          <Section title="✅ Pre-selected (selectedList)">
            <AppAutocompleteWithActions
              name="amenitiesActionsPreselected"
              control={control}
              label={t('label_room_facilities')}
              options={amenitiesData}
              multiple
              selectedList={[amenitiesData[0]]}
              itemActionHandler={() => {}}
            />
          </Section>

          <Section title="🚫 Disabled">
            <AppAutocompleteWithActions
              name="amenitiesActionsDisabled"
              control={control}
              label={t('label_room_facilities')}
              options={amenitiesData}
              multiple
              disabled
              selectedList={[amenitiesData[1]]}
              itemActionHandler={() => {}}
            />
          </Section>

          <Section title="❗ Error / required (validationProps)">
            <AppAutocompleteWithActions
              name="amenitiesActionsRequired"
              control={control}
              label={t('label_room_facilities')}
              options={amenitiesData}
              multiple
              itemActionHandler={() => {}}
              validationProps={{
                errors,
                errorMessages: {
                  amenitiesActionsRequired: { required: 'validation_service_required' }
                },
                getErrorMessage,
                validationRules: { required: true }
              }}
            />
          </Section>
        </div>
      </div>

      {/* Dialog opened by the "Add with dialog" example */}
      <AppDialog
        open={addDialogOpen}
        size="sm"
        title="Add included service"
        content={
          <FormControl fullWidth className="mt-4">
            <FormLabel>Service name</FormLabel>
            <OutlinedInput
              autoFocus
              value={addDialogValue}
              placeholder="Enter service name"
              onChange={(e) => setAddDialogValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddDialogSave();
                }
              }}
            />
          </FormControl>
        }
        actions={
          <div className="flex justify-end gap-3 w-full">
            <Button variant="outlined" onClick={closeAddDialog}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={!addDialogValue.trim()}
              onClick={handleAddDialogSave}
            >
              {t('save')}
            </Button>
          </div>
        }
        onClose={closeAddDialog}
      />

      {/* Add / edit dialog for AppAutocompleteWithActions */}
      <AppDialog
        open={amenityDialogOpen}
        size="sm"
        title={amenityEditId != null ? 'Edit amenity' : 'Add amenity'}
        content={
          <FormControl fullWidth className="mt-4">
            <FormLabel>Amenity name</FormLabel>
            <OutlinedInput
              autoFocus
              value={amenityDialogValue}
              placeholder="Enter amenity name"
              onChange={(e) => setAmenityDialogValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveAmenity();
                }
              }}
            />
          </FormControl>
        }
        actions={
          <div className="flex justify-end gap-3 w-full">
            <Button variant="outlined" onClick={closeAmenityDialog}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={!amenityDialogValue.trim()}
              onClick={saveAmenity}
            >
              {t('save')}
            </Button>
          </div>
        }
        onClose={closeAmenityDialog}
      />

      <DeleteDialog
        open={!!amenityDeleteTarget}
        title={t('delete_popup_title')}
        isLoading={false}
        onDelete={confirmDeleteAmenity}
        onCancel={() => setAmenityDeleteTarget(null)}
      />
    </div>
  );
}

export default AutocompleteComponent;
